import React, { useCallback, useMemo, useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import Select, {
  components,
  OptionProps,
  StylesConfig,
  MenuListComponentProps,
  ValueType,
} from "react-select";
import { FEATURE_TAG_MESSAGES } from "../../shared/featureTags.messages";
import { FEATURE_TAGS } from "../../witadmin/Common/constants";
import { FunctionalityReportFormType } from "../index";
import { ErrorFieldMessage } from "../shared/components/ErrorFieldMessage";

const messages = defineMessages({
  title: {
    id: "functionalityReport.filter.title",
    defaultMessage: "Functionalities",
  },
  placeholder: {
    id: "functionalityReport.filter.placeholder",
    defaultMessage: "Select functionalities...",
  },
  selectAll: {
    id: "functionalityReport.filter.selectAll",
    defaultMessage: "Select All",
  },
  selectNone: {
    id: "functionalityReport.filter.selectNone",
    defaultMessage: "Select None",
  },
  clear: {
    id: "functionalityReport.filter.clear",
    defaultMessage: "Clear",
  },
});

const WitLabel = styled.label`
  display: block;
  width: 100%;
  padding: 0;
  margin: 10px 0 0 0;
  font-size: 21px;
  line-height: inherit;
  color: #3a3f51;
  border: 0;
  border-bottom: 1px solid #e5e5e5;
`;

const customStyles: StylesConfig<Option, true> = {
  control: base => ({
    ...base,
    borderRadius: "5px",
    borderColor: "#ccc",
    boxShadow: "none",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "rgba(59,130,246,0.1)"
      : state.isFocused
      ? "rgba(59,130,246,0.05)"
      : "transparent",
    color: "inherit",
  }),
  multiValue: () => ({
    display: "none",
  }),
};

const CheckboxOption = (props: OptionProps<Option, true>) => {
  const { isSelected, label } = props;
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={isSelected} readOnly />
        <span>{label}</span>
      </div>
    </components.Option>
  );
};

const CustomValueContainer = (props: any) => {
  const selected = props.getValue() as Option[];
  const summary = selected.length === 0 ? props.children : selected.map(s => s.label).join(", ");
  return <components.ValueContainer {...props}>{summary}</components.ValueContainer>;
};

type Option = { label: string; value: string };

export function FunctionalityFilter({
  register,
  setValue,
  formState: { errors },
}: UseFormReturn<FunctionalityReportFormType>) {
  const [selected, setSelected] = useState<Option[]>([]);
  const intl = useIntl();

  const functionalities = useMemo(() => {
    // react-select types `label` as string, so formatMessage (not <FormattedMessage/>).
    return Object.entries(FEATURE_TAGS).map(([key, value]) => ({
      label: intl.formatMessage(FEATURE_TAG_MESSAGES[key as keyof typeof FEATURE_TAGS]),
      value,
    }));
  }, [intl]);

  const handleOnChange = useCallback(
    (options: ValueType<Option, true>) => {
      setSelected((options || []) as Option[]);
      setValue(
        "functionalities",
        (options as Option[]).map(it => it.value),
        { shouldValidate: true }
      );
    },
    [setValue]
  );

  const CustomMenuList = (props: MenuListComponentProps<Option, true>) => {
    const { selectProps } = props;
    const selectedValues = (selectProps.value || []) as Option[];
    const allSelected = selectedValues.length === (selectProps.options?.length ?? 0);

    const handleSelectAll = useCallback(() => {
      const newValue = allSelected ? [] : (selectProps.options as Option[]);
      // @ts-ignore
      selectProps.onChange?.(newValue, {
        action: allSelected ? "deselect-option" : "select-option",
      });
      setValue(
        "functionalities",
        newValue.map(it => it.value),
        { shouldValidate: true }
      );
    }, [allSelected, selectProps]);

    const handleClear = useCallback(() => {
      // @ts-ignore
      selectProps.onChange?.([], { action: "clear" });
      setValue("functionalities", [], { shouldValidate: true });
    }, [selectProps]);

    return (
      <components.MenuList {...props}>
        <div className="flex justify-between px-3 py-1 text-sm border-b border-gray-200">
          <button type="button" className="text-primary hover:underline" onClick={handleSelectAll}>
            {allSelected ? (
              <FormattedMessage {...messages.selectNone} />
            ) : (
              <FormattedMessage {...messages.selectAll} />
            )}
          </button>
          <button type="button" className="text-gray-500 hover:underline" onClick={handleClear}>
            <FormattedMessage {...messages.clear} />
          </button>
        </div>
        {props.children}
      </components.MenuList>
    );
  };

  return (
    <>
      <WitLabel>
        <FormattedMessage {...messages.title} />
      </WitLabel>
      <div className="w-full space-y-2">
        <Select<Option, true>
          {...register("functionalities")}
          options={functionalities}
          value={selected}
          onChange={handleOnChange}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{
            Option: CheckboxOption,
            MenuList: CustomMenuList,
            ValueContainer: CustomValueContainer,
          }}
          styles={customStyles}
          placeholder={<FormattedMessage {...messages.placeholder} />}
        />
        <ErrorFieldMessage errors={errors} name={`functionalities`} />
      </div>
    </>
  );
}
